import { autoUpdate, flip, shift, useFloating, UseFloatingOptions } from "@floating-ui/react";
import classNames from "classnames";
import FocusTrap from "focus-trap-react";
import compact from "lodash/compact";
import Calendar, { CalendarProps } from "PFComponents/calendar/calendar";
import { useModalContext } from "PFComponents/modal";
import { InputFieldSet, InputFieldSetProps } from "PFComponents/text/input_field_set";
import { utc } from "PFCore/helpers/date";
import { isActionKeyPressed } from "PFCore/helpers/is_action_key_pressed";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { IconName } from "PFTheme/graphics/icons";
import { EventKey } from "PFTypes/event_key";
import { CSSProperties, KeyboardEvent, RefObject, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useClickAway } from "react-use";

import css from "./inline_calendar.module.scss";

type InlineCalendarProps = {
  letClear?: boolean;
  onRestore?: () => void;
  handleChange?: (date: string | null) => void;
  showTime?: boolean;
  displayValue?: string | null;
  selectedDate?: string | null;
  onKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void;
  onClickOutside?: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
  className?: string;
  inputClassName?: string;
  inputStyle?: CSSProperties;
  disabled?: boolean;
  calendarProps?: CalendarProps;
  calendarStyle?: CSSProperties;
  floatingOptions?: Partial<UseFloatingOptions>;
  shouldCloseOnSelection?: boolean;
  portalRef?: RefObject<HTMLElement | null>;
  clearDate?: string;
  inputRef?: RefObject<HTMLInputElement | HTMLTextAreaElement>;
} & Pick<
  InputFieldSetProps,
  | "label"
  | "title"
  | "locked"
  | "lockedTip"
  | "disabled"
  | "qaId"
  | "error"
  | "warning"
  | "placeholder"
  | "required"
  | "variant"
  | "tabIndex"
  | "style"
> &
  Pick<CalendarProps, "minDate" | "maxDate">;

export const InlineCalendar = ({
  inputClassName,
  letClear,
  onRestore,
  handleChange,
  showTime,
  displayValue,
  selectedDate,
  label,
  title,
  locked,
  minDate,
  maxDate,
  lockedTip,
  disabled,
  qaId,
  error,
  warning,
  placeholder,
  required,
  calendarProps,
  calendarStyle,
  floatingOptions,
  shouldCloseOnSelection = true,
  portalRef: propPortalRef,
  clearDate,
  variant,
  tabIndex,
  style,
  inputRef
}: InlineCalendarProps) => {
  const [selectedDay, setSelectedDay] = useState(selectedDate);
  const [showCalendar, setShowCalendar] = useState(false);

  const { dateFormatString, dateTimeFormatString } = useDateFormatter();
  const { portalRef: modalPortalRef } = useModalContext();

  const shouldFocusInputRef = useRef(false);
  const portalRef = propPortalRef ?? modalPortalRef;

  const { refs, floatingStyles } = useFloating({
    strategy: "fixed",
    placement: "bottom",
    middleware: [
      flip(),
      shift({
        mainAxis: true,
        crossAxis: true
      })
    ],
    whileElementsMounted: autoUpdate,
    ...floatingOptions
  });

  useEffect(() => {
    setSelectedDay(selectedDate);
  }, [selectedDate]);

  useEffect(() => {
    if (!showCalendar && shouldFocusInputRef.current) {
      shouldFocusInputRef.current = false;
      (refs.reference as RefObject<HTMLDivElement>).current?.focus();
    }
  }, [showCalendar]);

  const handleHideCalendar = (refocusInput?: boolean) => {
    shouldFocusInputRef.current = !!refocusInput;
    setShowCalendar(false);
  };

  const handleShowCalendar = () => {
    setShowCalendar(true);
  };

  const handleToggleCalendar = () => {
    if (showCalendar) {
      handleHideCalendar(true);
    } else {
      handleShowCalendar();
    }
  };

  const handleCalendarChange = (date) => {
    if (shouldCloseOnSelection) {
      setSelectedDay(date);
      handleHideCalendar(true);
    }

    handleChange?.(date);
  };

  const handleCalendarKeyDown = (event) => {
    if (event.key === EventKey.Escape) {
      handleHideCalendar(true);
    }
  };

  const handleInputKeyDown = (event) => {
    if (isActionKeyPressed(event)) {
      handleShowCalendar();
    } else if (event.key === EventKey.Escape) {
      handleHideCalendar();
    }
  };

  const value = useMemo(() => {
    if (displayValue) {
      return displayValue;
    }

    const fmt = showTime ? dateTimeFormatString : dateFormatString;
    return selectedDate ? utc(selectedDate).format(fmt) : "";
  }, [displayValue, showTime, dateTimeFormatString, dateFormatString, selectedDate]);

  useClickAway(refs.reference as RefObject<HTMLDivElement>, (e) => {
    if (refs.floating.current?.contains(e.target as Node)) {
      return;
    }

    handleHideCalendar();
  });

  const CalendarRenderer = (
    <FocusTrap focusTrapOptions={{ allowOutsideClick: true, returnFocusOnDeactivate: false }}>
      <div
        role="dialog"
        className={css.calendar}
        ref={refs.setFloating}
        style={{ ...calendarStyle, ...floatingStyles, zIndex: 20000 }}
      >
        <Calendar
          {...calendarProps}
          minDate={minDate}
          maxDate={maxDate}
          showTime={showTime}
          selectedDate={selectedDay}
          handleChange={handleCalendarChange}
          onKeyDown={handleCalendarKeyDown}
        />
      </div>
    </FocusTrap>
  );

  const actionIcons = useMemo(
    () =>
      compact([
        onRestore && {
          name: "history" as IconName,
          onClick: onRestore
        },
        value &&
          letClear && {
            name: "filter-clean" as IconName,
            onClick: (event) => {
              event.preventDefault();
              handleChange?.(clearDate ? clearDate : null);
            }
          }
      ]),
    [handleChange, onRestore, value, letClear, clearDate]
  );

  return (
    <div
      className={classNames(css.root, { [css.tableVariant]: variant === "table" })}
      style={style}
      ref={refs.setReference}
      tabIndex={-1}
    >
      <InputFieldSet
        ref={inputRef}
        inputClassName={inputClassName}
        aria-expanded={showCalendar}
        aria-haspopup="dialog"
        icon="calendar"
        label={label}
        value={value}
        title={title ?? value}
        locked={locked}
        lockedTip={lockedTip}
        disabled={disabled}
        qaId={qaId}
        error={error}
        placeholder={placeholder}
        required={required}
        warning={warning}
        onClick={handleToggleCalendar}
        onKeyDown={handleInputKeyDown}
        actions={actionIcons}
        variant={variant}
        tabIndex={tabIndex}
      />
      {showCalendar &&
        !disabled &&
        (portalRef?.current ? ReactDOM.createPortal(CalendarRenderer, portalRef.current) : CalendarRenderer)}
    </div>
  );
};
